import turboInit from "@/javascripts/shared/utils/turbo-init"

const WRAPPER_SELECTOR = `.js--floating-form-label`
const FIELD_SELECTOR = `input, textarea, select`

// Resolve the floating-label wrapper + its field from an event target, or null
// if the event didn't originate inside a floating label.
const resolve = (target) => {
  if (!target || typeof target.closest !== `function`) return null
  const inputEl = target.closest(FIELD_SELECTOR)
  if (!inputEl) return null
  const el = inputEl.closest(WRAPPER_SELECTOR)
  if (!el) return null
  // Choices.js renders its own search input inside the widget; it is always
  // empty, so read the value off the original <select> it wraps instead.
  const widget = inputEl.closest(`.choices`)
  return { el, inputEl: widget?.querySelector(`select`) || inputEl }
}

const syncHasValue = (el, inputEl) => {
  // Check value OR if the browser visually marks it as autofilled
  // Note: Direct check for :-webkit-autofill might not be reliable in all contexts,
  // the animationstart event is the primary mechanism here.
  if (inputEl.value || inputEl.matches(`:-webkit-autofill`)) {
    el.classList.add(`-has-value`)
  } else {
    el.classList.remove(`-has-value`)
  }
}

// Set the initial state for every floating label currently in the DOM. Safe to
// call repeatedly (idempotent) — used on load and after Turbo Frame updates.
const syncAll = () => {
  document.querySelectorAll(WRAPPER_SELECTOR).forEach((el) => {
    const inputEl = el.querySelector(FIELD_SELECTOR)
    if (inputEl) syncHasValue(el, inputEl)
  })
}

const onChange = (event) => {
  const match = resolve(event.target)
  if (match) syncHasValue(match.el, match.inputEl)
}

// CSS animation triggered by autofill
const onAnimationStart = (event) => {
  if (event.animationName !== `onAutoFillStart`) return
  const match = resolve(event.target)
  if (match) syncHasValue(match.el, match.inputEl)
}

// `focusin`/`focusout` are the bubbling counterparts of `focus`/`blur`, so they
// can be delegated from the document.
const onFocusIn = (event) => {
  const match = resolve(event.target)
  if (match) match.el.classList.add(`-has-value`)
}

const onFocusOut = (event) => {
  const match = resolve(event.target)
  if (match && !match.inputEl.value) match.el.classList.remove(`-has-value`)
}

turboInit(() => {
  // Event delegation on the document keeps the behaviour working for inputs
  // inserted later by a Turbo Frame update (e.g. the "Bevaka sökningen" form,
  // which lives inside the mission search results frame) without re-binding
  // per-element listeners that would be lost when the frame DOM is replaced.
  document.addEventListener(`change`, onChange)
  document.addEventListener(`animationstart`, onAnimationStart)
  document.addEventListener(`focusin`, onFocusIn)
  document.addEventListener(`focusout`, onFocusOut)

  // Initial value state for already-rendered labels. `turbo:load` does not fire
  // for frame-only updates, so also re-sync whenever a frame finishes loading.
  syncAll()
  document.addEventListener(`turbo:frame-load`, syncAll)

  return () => {
    document.removeEventListener(`change`, onChange)
    document.removeEventListener(`animationstart`, onAnimationStart)
    document.removeEventListener(`focusin`, onFocusIn)
    document.removeEventListener(`focusout`, onFocusOut)
    document.removeEventListener(`turbo:frame-load`, syncAll)
  }
})
